<template>
<div class="cfd-specification" :data-mq="mediaQuery">
    <div class="cfd-filters">
        <div class="cfd-filter cfd-filter--platform">
            <PlatformFilter/>
        </div>
        <div class="cfd-filter cfd-filter--group">
            <GroupFilter/>
        </div>
        <div class="cfd-filter cfd-filter--subgroups">
            <SubgroupsFilter/>
        </div>
        <div class="cfd-filter cfd-filter--columns">
            <ColumnsFilter/>
        </div>
        <div class="cfd-filter cfd-filter--filter">
            <InstrumentFilter/>
        </div>
    </div>
    <div class="cfd-instruments">
        <component v-if="hasDataToShow" :is="activeTable"/>
        <div v-else class="cfd-empty">{{ t('No instruments to show') }}</div>
        <div v-if="isDataLoading" class="cfd-loader"></div>
    </div>
</div>
</template>

<script>
import PlatformFilter from './filters/PlatformFilter.vue';
import GroupFilter from './filters/GroupFilter.vue';
import SubgroupsFilter from './filters/SubgroupsFilter.vue';
import ColumnsFilter from './filters/ColumnsFilter.vue';
import InstrumentFilter from './filters/InstrumentFilter.vue';
import InstrumentsMobile from './mobile/InstrumentsList.vue';
import InstrumentsDesktop from './desktop/InstrumentTable.vue';
import debounce from 'lodash.debounce';
import queryString from 'query-string';
import i18n from '../i18n';

export default {
    name: 'CfdSpecification',

    components: {
        PlatformFilter,
        GroupFilter,
        SubgroupsFilter,
        ColumnsFilter,
        InstrumentFilter,
        InstrumentsMobile,
        InstrumentsDesktop
    },

    props: {
        platformCode: {
            type: String,
            required: true
        },
        lang: {
            type: String,
            required: true
        },
        broker: {
            type: String,
            required: true
        },
        currency: {
            type: String,
            required: true
        },
        cacheTtl: {
            type: [String, Number],
            default: 1000 * 60 * 60 * 24
        },
        source: {
            type: String,
            default: 'https://gaterest.fxclub.org/specification'
        },
        headerOffset: {
            type: String,
            default: '0'
        }
    },

    computed: {
        activeTable () {
            return this.$store.state.widgetWidth < 720
                ? 'InstrumentsMobile'
                : 'InstrumentsDesktop';
        },

        mediaQuery () {
            return this.$store.getters.mediaQuery.join(' ');
        },

        hasDataToShow () {
            return !!this.$store.state.instrumentsToShow.length && !!this.$store.state.columnsToShow.length;
        },

        resultedPlatformCode () {
            const platformFromQuery = queryString.parse(location.search).platform;
            switch (platformFromQuery) {
                case 'metatrader':
                    return 'mt4_instant';
                case 'metatrader-market':
                    return 'mt4_market';
                case 'metatrader-5':
                    return 'mt5_instant';
                case 'metatrader-5-market':
                    return 'mt5_market';
                default:
                    return platformFromQuery;
            }
        },

        isDataLoading () {
            return this.$store.state.isDataLoading;
        }
    },

    methods: {
        updateWidgetWidth () {
            this.$store.commit('setWidgetWidth', { widgetWidth: this.$el.clientWidth });
        },

        // TODO: can be cached somehow?
        t (str) {
            return i18n(str, this.$store.state.lang);
        }
    },

    created () {
        const { lang, broker, currency, resultedPlatformCode: platformCode, cacheTtl, source, headerOffset } = this;
        this.$store.dispatch('init', { lang, broker, currency, platformCode, cacheTtl, source, headerOffset });
        window.addEventListener('resize', debounce(this.updateWidgetWidth, 50));
    },

    mounted () {
        this.updateWidgetWidth();
    }
}
</script>

<style src="../assets/css/main.css"></style>
<style scoped>
.cfd-specification {
    margin-bottom: 24px;
    font-size: 14px;
    font-family: sans-serif;
    line-height: 1.428571; /* 20/14 */
    color: rgba(0, 0, 0, .87);
    background-color: #fff;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

.cfd-loader {
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(255, 255, 255, .5);
}

.cfd-loader::before,
.cfd-loader::after {
    content: "";
    position: absolute;
    z-index: 1;
    top: 24px;
    left: 50%;
    margin-top: -16px;
    margin-left: -16px;
    border-width: 4px;
    border-style: solid;
    border-radius: 50%;
    width: 24px;
    height: 24px;
}

.cfd-loader::before {
    border-color: rgba(0, 0, 0, .12);
}

.cfd-loader::after {
    border-color: #f60 transparent transparent transparent;
    animation: rotate 1s infinite;
}

@keyframes rotate {
    0% {
        transform: rotate(0);
    }
    100% {
        transform: rotate(360deg);
    }
}

.cfd-filters {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -10px 20px;
}

.cfd-filter {
    box-sizing: border-box;
    margin-bottom: 10px;
    padding-right: 10px;
    padding-left: 10px;
}

.cfd-filter--platform,
.cfd-filter--group,
.cfd-filter--subgroups,
.cfd-filter--columns,
.cfd-filter--filter {
    width: 100%;
}

.cfd-filter--platform {
    z-index: 6;
}

.cfd-filter--group {
    z-index: 5;
}

.cfd-filter--subgroups {
    z-index: 4;
}

.cfd-filter--columns {
    z-index: 3;
}

.cfd-filter--filter {
    z-index: 2;
}

.cfd-instruments {
    min-height: 48px;
    box-shadow: 0 0 4px rgba(0, 0, 0, .14), 0 3px 4px rgba(0, 0, 0, .12);
}

[data-mq*="600"] .cfd-filter--platform,
[data-mq*="600"] .cfd-filter--group,
[data-mq*="600"] .cfd-filter--subgroups,
[data-mq*="600"] .cfd-filter--columns {
    width: 50%;
}

[data-mq*="720"] .cfd-filter--platform,
[data-mq*="720"] .cfd-filter--group,
[data-mq*="720"] .cfd-filter--subgroups {
    width: 33.333%;
}

[data-mq*="720"] .cfd-filter--filter {
    width: 50%;
}

[data-mq*="720"] .cfd-filter--columns {
    order: 1;
}

[data-mq*="1024"] .cfd-filter--columns {
    margin-left: auto;
    width: 33.333%;
}

[data-mq*="1024"] .cfd-filter--filter {
    width: 33.333%;
}

.cfd-instruments {
    position: relative;
}

.cfd-empty {
    padding: 14px 16px;
    line-height: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/CfdSpecification.vue